import React, { createContext, useContext, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLogin, setLogout, setRefreshToken } from "state/authSlice";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.authReducer.user);
  const token = useSelector((state) => state.authReducer.token);
  const refresh = useSelector((state) => state.authReducer.refresh);
  const isAuthenticated = useSelector((state) => state.authReducer.auth);
  const permissions = useSelector((state) => state.authReducer.permissions);
  const modules = useSelector((state) => state.authReducer.modules);
  const businessId = useSelector((state) => state.authReducer.business_id);
  const businessBranchId = useSelector((state) => state.authReducer.business_branch_id);

  // Keep sessionStorage in sync with Redux persisted state on mount
  useEffect(() => {
    if (token) {
      sessionStorage.setItem("access_token", token);
    }
    if (refresh) {
      sessionStorage.setItem("refresh_token", refresh);
    }
  }, [token, refresh]);

  const login = useCallback(
    (payload) => {
      dispatch(setLogin(payload));
      sessionStorage.setItem("access_token", payload.token);
      sessionStorage.setItem("refresh_token", payload.refresh);
    },
    [dispatch]
  );

  const logout = useCallback(() => {
    dispatch(setLogout());
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
  }, [dispatch]);

  const updateToken = useCallback(
    (accessToken) => {
      dispatch(setRefreshToken(accessToken));
      sessionStorage.setItem("access_token", accessToken);
    },
    [dispatch]
  );

  const hasPermission = useCallback(
    (permissionKey) => {
      if (!permissions) return false;
      return !!permissions[permissionKey];
    },
    [permissions]
  );

  const hasModule = useCallback(
    (moduleKey) => {
      if (!modules) return false;
      return !!modules[moduleKey];
    },
    [modules]
  );

  const value = {
    user,
    token,
    refresh,
    isAuthenticated,
    permissions,
    modules,
    businessId,
    businessBranchId,
    login,
    logout,
    updateToken,
    hasPermission,
    hasModule,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthProvider;
